import Apis from '@/Apis/_'
import { getUser } from '@/utils/common'
import { PushNotificationResult, WebPushSubscriptionPayload } from '@/types/interfaces/PushNotification'

const SERVICE_WORKER_URL = '/push-service-worker.js'

const SERVICE_WORKER_SCOPE = '/push/'

function urlBase64ToUint8Array(base64UrlKey: string) {
  const padding = '='.repeat((4 - (base64UrlKey.length % 4)) % 4)
  const base64 = (base64UrlKey + padding).replace(/-/g, '+').replace(/_/g, '/')

  return Uint8Array.from(window.atob(base64), char => char.charCodeAt(0))
}

function readApplicationServerKey() {
  const publicKey: string = (window as any).WEBPUSH_VAPID_PUBLIC_KEY

  if (!publicKey) {
    throw new Error('WEBPUSH_VAPID_PUBLIC_KEY não configurada no env deste ambiente')
  }

  return urlBase64ToUint8Array(publicKey)
}

function buildSubscriptionPayload(subscription: PushSubscription): WebPushSubscriptionPayload {
  const { keys } = subscription.toJSON()

  return {
    usuarioId: getUser().profile.sub,
    endpoint: subscription.endpoint,
    keys: {
      p256dh: (keys && keys.p256dh) || '',
      auth: (keys && keys.auth) || '',
    },
    dispositivo: navigator.userAgent,
  }
}

function waitForActiveWorker(registration: ServiceWorkerRegistration): Promise<ServiceWorkerRegistration> {
  const installingWorker = registration.installing || registration.waiting

  if (registration.active || !installingWorker) {
    return Promise.resolve(registration)
  }

  return new Promise((resolve) => {
    installingWorker.addEventListener('statechange', () => {
      if (installingWorker.state === 'activated') {
        resolve(registration)
      }
    })
  })
}

class PushNotificationService {
  get isSupported() {
    return 'serviceWorker' in navigator && 'PushManager' in window && 'Notification' in window
  }

  get isBlocked() {
    return this.isSupported && Notification.permission === 'denied'
  }

  async getSubscription() {
    if (!this.isSupported) {
      return null
    }

    const registration = await navigator.serviceWorker.getRegistration(SERVICE_WORKER_SCOPE)

    if (!registration) {
      return null
    }

    return registration.pushManager.getSubscription()
  }

  async subscribe(): Promise<PushNotificationResult> {
    if (!this.isSupported) {
      return { success: false, message: 'Este navegador não suporta notificações no dispositivo.' }
    }

    const permission = await Notification.requestPermission()

    if (permission !== 'granted') {
      return { success: false, message: 'Permissão de notificação negada nas configurações do navegador.' }
    }

    try {
      const registration = await this.registerServiceWorker()
      const subscription = await this.getOrCreateSubscription(registration)
      const response = await Apis.Notificacoes.WebPushApi.postSubscription(buildSubscriptionPayload(subscription))

      if (!response.success) {
        return { success: false, message: response.message || 'Não foi possível registrar este dispositivo para receber notificações.' }
      }

      return { success: true }
    } catch (error) {
      console.error('Erro ao ativar as notificações no dispositivo:', error)

      return { success: false, message: 'Não foi possível ativar as notificações neste dispositivo.' }
    }
  }

  async unsubscribe(): Promise<PushNotificationResult> {
    const subscription = await this.getSubscription()

    if (!subscription) {
      return { success: true }
    }

    try {
      await subscription.unsubscribe()

      return { success: true }
    } catch (error) {
      console.error('Erro ao desativar as notificações no dispositivo:', error)

      return { success: false, message: 'Não foi possível desativar as notificações neste dispositivo.' }
    }
  }

  private async registerServiceWorker() {
    const registration = await navigator.serviceWorker.register(SERVICE_WORKER_URL, { scope: SERVICE_WORKER_SCOPE })

    return waitForActiveWorker(registration)
  }

  private async getOrCreateSubscription(registration: ServiceWorkerRegistration) {
    const currentSubscription = await registration.pushManager.getSubscription()

    if (currentSubscription) {
      return currentSubscription
    }

    return registration.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: readApplicationServerKey(),
    })
  }
}

// eslint-disable-next-line import/prefer-default-export
export const pushNotificationService = new PushNotificationService()
